import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { auth, functions, db } from "../firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import "./ManageAdmins.css"; // reuse same styling

export default function ManageBusinessOwners() {
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [selectedOwnerId, setSelectedOwnerId] = useState(""); // value in the <select>
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // --- Load data -------------------------------------------------------------

  const loadBusinesses = async () => {
    const q = query(collection(db, "businesses"), orderBy("business_name"));
    const snap = await getDocs(q);
    const list = [];
    snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
    setBusinesses(list);

    if (!selectedBusinessId && list.length > 0) {
      setSelectedBusinessId(list[0].id);
    }
  };

  const loadUsers = async () => {
    if (!auth.currentUser) return;
    const token = await auth.currentUser.getIdToken(true);
    const listUsersPublic = httpsCallable(functions, "listUsersPublic");
    const res = await listUsersPublic({ __idToken: token });
    const arr = Array.isArray(res.data) ? res.data : [];

    // Normalise: always have email + (maybe) uid + role
    const normalized = arr.map((u) => ({
      uid: u.uid || null,
      email: (u.email || "").toLowerCase(),
      role: u.role || "user",
    }));

    setUsers(normalized);
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadBusinesses(), loadUsers()]);
      } catch (e) {
        console.error(e);
        alert(e.message || "Error loading data");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Derived values --------------------------------------------------------

  const selectedBusiness =
    businesses.find((b) => b.id === selectedBusinessId) || null;

  const ownerId = selectedBusiness?.ownerId || null;
  const ownerEmailField = selectedBusiness?.ownerEmail || null;

  // Try to resolve current owner to a user:
  // 1. match by uid (preferred – what ownerId should be)
  // 2. if that fails and we have ownerEmail stored, match by email
  let ownerUser = null;
  if (ownerId) {
    ownerUser = users.find((u) => u.uid === ownerId) || null;
  }
  if (!ownerUser && ownerEmailField) {
    ownerUser = users.find(
      (u) => u.email === ownerEmailField.toLowerCase()
    ) || null;
  }

  // Human-readable "Current owner" string
  let currentOwnerText = "None set";
  if (ownerUser) {
    currentOwnerText = ownerUser.uid
      ? `${ownerUser.email} (${ownerUser.uid})`
      : ownerUser.email;
  } else if (ownerEmailField) {
    currentOwnerText = ownerEmailField;
  } else if (ownerId) {
    // we see an ownerId in Firestore but can't map it to a user list entry
    currentOwnerText = `UID: ${ownerId} (user not in list)`;
  }

  // Keep dropdown in sync with whatever we think the actual owner is
  useEffect(() => {
    if (ownerUser && ownerUser.uid) {
      setSelectedOwnerId(ownerUser.uid);
    } else {
      setSelectedOwnerId("");
    }
  }, [selectedBusinessId, ownerUser?.uid]);

  // --- Actions ----------------------------------------------------------------

  const handleAssign = async () => {
    if (!selectedBusinessId || !selectedOwnerId) return;

    const pickedUser =
      users.find((u) => u.uid === selectedOwnerId) || null;

    if (!pickedUser) {
      alert("Could not find that user in the list.");
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "businesses", selectedBusinessId), {
        ownerId: pickedUser.uid,          // use UID as the primary key
        ownerEmail: pickedUser.email,     // store email for easy display
      });

      // Update local state so UI reflects the change immediately
      setBusinesses((prev) =>
        prev.map((b) =>
          b.id === selectedBusinessId
            ? { ...b, ownerId: pickedUser.uid, ownerEmail: pickedUser.email }
            : b
        )
      );
    } catch (e) {
      console.error(e);
      alert(e.message || "Error assigning owner");
    } finally {
      setSaving(false);
    }
  };

  // --- Render -----------------------------------------------------------------

  if (loading) {
    return (
      <div className="manage-admins">
        <div className="card">
          <h2>Business Owners</h2>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-admins">
      <div className="card">
        <h2>Business Owners</h2>

        {/* Choose business */}
        <div className="controls">
          <label>
            Business:&nbsp;
            <select
              className="input"
              value={selectedBusinessId}
              onChange={(e) => setSelectedBusinessId(e.target.value)}
            >
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.business_name || b.id}
                </option>
              ))}
            </select>
          </label>
        </div>

        {selectedBusiness && (
          <>
            <p style={{ marginTop: 8, marginBottom: 12 }}>
              <strong>Current owner:</strong> {currentOwnerText}
            </p>

            {/* Assign owner */}
            <div className="controls">
              <label>
                Assign to user:&nbsp;
                <select
                  className="input"
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                >
                  <option value="">— Select a user —</option>
                  {users.map((u) =>
                    u.uid ? (
                      <option key={u.uid} value={u.uid}>
                        {u.email} ({u.role})
                      </option>
                    ) : null
                  )}
                </select>
              </label>

              <button
                className="btn btn-primary"
                onClick={handleAssign}
                disabled={saving || !selectedOwnerId}
              >
                {saving ? "Saving…" : "Save ownerId"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
